import { useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

interface IBatch {
  writes: number;
  refreshed: Promise<void>;
  resolve: () => void;
}

function newBatch(): IBatch {
  let resolve!: () => void;
  const refreshed = new Promise<void>((done) => { resolve = done; });
  return { writes: 0, refreshed, resolve };
}

/**
 * Refetches a cached list once the last write to it settles, not after each:
 * an earlier refetch would bring back what a write still in flight has edited
 * (a second quick delete reappearing).
 *
 * `beginWrite(key)` counts a write in; the function it returns counts it out,
 * and resolves once the list has been refetched after every write of the batch.
 */
export function useRefreshAfterWrites() {
  const queryClient = useQueryClient();
  const batches = useRef(new Map<string, IBatch>());

  return function beginWrite(queryKey: QueryKey): () => Promise<void> {
    const id = JSON.stringify(queryKey);
    const batch = batches.current.get(id) ?? newBatch();
    batches.current.set(id, batch);
    batch.writes += 1;
    return async () => {
      batch.writes -= 1;
      if (batch.writes === 0) {
        batches.current.delete(id);
        await queryClient.invalidateQueries({ queryKey });
        batch.resolve();
      }
      return batch.refreshed;
    };
  };
}
